import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BarChart3, Trophy, Handshake, Sparkles, TrendingUp, Users, DollarSign, Percent, Filter, CalendarDays, Award } from "lucide-react";

const ESTAGIO_META = {
  // N1 (Prospecção/Fechamento)
  indicacao: { label: "Indicação", color: "bg-sky-500" },
  prospeccao: { label: "Prospecção", color: "bg-sky-500" },
  qualificado: { label: "Qualificado", color: "bg-cyan-500" },
  reuniao: { label: "Reunião", color: "bg-indigo-500" },
  proposta: { label: "Proposta", color: "bg-violet-500" },
  documentacao: { label: "Documentação", color: "bg-amber-500" },
  contrato_assinado: { label: "Contrato Assinado", color: "bg-emerald-500" },
  perdido: { label: "Perdido", color: "bg-red-500" },
  // N2 (Requalificação)
  entrada: { label: "Requalif. · Entrada", color: "bg-fuchsia-500" },
  nutricao: { label: "Requalif. · Nutrição", color: "bg-fuchsia-600" },
  monitoramento_gatilho: { label: "Requalif. · Mon. Gatilho", color: "bg-purple-500" },
  gatilho_ativado: { label: "Requalif. · Gatilho Ativado", color: "bg-pink-500" },
  requalificado: { label: "Requalificado", color: "bg-rose-500" },
  // Legacy (fallback para cards antigos ainda não migrados)
  indicacao_prospeccao: { label: "Indicação / Prospecção", color: "bg-sky-500" },
  qualificacao: { label: "Qualificação", color: "bg-cyan-500" },
  requalificacao: { label: "Requalificação", color: "bg-fuchsia-500" },
};

function KpiCard({ label, value, icon: Icon, tone = "sky", subtitle }) {
  const bg = tone === "emerald" ? "bg-emerald-600 text-white" : tone === "amber" ? "bg-amber-500 text-white" : tone === "sky-solid" ? "bg-sky-600 text-white" : "bg-white border border-slate-200";
  return (
    <div className={`rounded-2xl p-5 ${bg} shadow-sm`}>
      <div className="flex items-center justify-between">
        <div className={`text-[10px] uppercase font-bold tracking-wider ${tone === "sky" ? "text-slate-500" : "opacity-90"}`}>{label}</div>
        <Icon className="w-5 h-5 opacity-80" />
      </div>
      <div className={`text-3xl font-bold mt-2 ${tone === "sky" ? "text-slate-900" : ""}`}>{value}</div>
      {subtitle && <div className={`text-[11px] mt-1 ${tone === "sky" ? "text-slate-500" : "opacity-90"}`}>{subtitle}</div>}
    </div>
  );
}

function _formatData(d) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("pt-BR");
}

function _firstDayOfMonth(y, m) {
  return `${y}-${String(m).padStart(2, "0")}-01`;
}
function _lastDayOfMonth(y, m) {
  const last = new Date(y, m, 0).getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
}

const MESES = [
  { v: 1, l: "Janeiro" }, { v: 2, l: "Fevereiro" }, { v: 3, l: "Março" },
  { v: 4, l: "Abril" }, { v: 5, l: "Maio" }, { v: 6, l: "Junho" },
  { v: 7, l: "Julho" }, { v: 8, l: "Agosto" }, { v: 9, l: "Setembro" },
  { v: 10, l: "Outubro" }, { v: 11, l: "Novembro" }, { v: 12, l: "Dezembro" },
];

export default function CelDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const _hoje = useMemo(() => new Date(), []);
  const [modo, setModo] = useState("mes"); // mes | custom | todos
  const [mesSel, setMesSel] = useState(_hoje.getMonth() + 1);
  const [anoSel, setAnoSel] = useState(_hoje.getFullYear());
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [rankingTab, setRankingTab] = useState("fechamentos"); // fechamentos | indicacoes | vendedoras

  const isGestaoOuDiretor = user?.role === "gestao_cel" || user?.role === "diretoria";

  const anosDisp = useMemo(() => {
    const y = _hoje.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, [_hoje]);

  const { di, df } = useMemo(() => {
    if (modo === "todos") return { di: "", df: "" };
    if (modo === "custom") return { di: dataInicio || "", df: dataFim || "" };
    return { di: _firstDayOfMonth(anoSel, mesSel), df: _lastDayOfMonth(anoSel, mesSel) };
  }, [modo, mesSel, anoSel, dataInicio, dataFim]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (di) params.set("data_inicio", di);
    if (df) params.set("data_fim", df);
    api.get(`/cel/dashboard?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [di, df]);

  if (loading) return <div className="text-center py-20 text-slate-400">Carregando dashboard...</div>;
  if (!data) return <div className="text-center py-20 text-slate-400">Sem dados</div>;

  const { kpis, ranking_fechamentos = [], ranking_indicacoes = [], ranking_vendedoras = [], funil } = data;
  const isVendScope = data?.scope === "vendedora";
  const maxQtd = Math.max(1, ...Object.values(funil).map((f) => f.qtd));

  const activeRanking =
    rankingTab === "fechamentos" ? ranking_fechamentos :
    rankingTab === "indicacoes" ? ranking_indicacoes :
    ranking_vendedoras;

  return (
    <div className="space-y-6" data-testid="cel-dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-sky-600" /> Dashboard CeL Consultoria
          </h1>
          <p className="text-sm text-slate-500 mt-1">Visão executiva do funil de consultoria + indicadores + ranking de vendedoras CeL.</p>
        </div>
        {/* Filtros de período */}
        <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-filters">
          <div className="text-xs uppercase font-bold text-slate-500 tracking-wider mr-1"><Filter className="inline w-3 h-3" /> Período:</div>
          <div className="flex bg-slate-100 rounded-lg p-1" data-testid="filter-modo">
            {[
              { v: "mes", l: "Mês" },
              { v: "custom", l: "Personalizado" },
              { v: "todos", l: "Todos" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setModo(o.v)}
                className={`px-3 py-1 text-xs font-bold rounded-md transition ${modo === o.v ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`filter-modo-${o.v}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          {modo === "mes" && (
            <>
              <Select value={String(mesSel)} onValueChange={(v) => setMesSel(Number(v))}>
                <SelectTrigger className="w-36" data-testid="filter-mes"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESES.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(anoSel)} onValueChange={(v) => setAnoSel(Number(v))}>
                <SelectTrigger className="w-24" data-testid="filter-ano"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {anosDisp.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {modo === "custom" && (
            <>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="w-40" data-testid="filter-data-inicio" />
              <span className="text-slate-400 text-xs">até</span>
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="w-40" data-testid="filter-data-fim" />
            </>
          )}
          {(di || df) && (
            <span className="text-[11px] text-slate-500"><CalendarDays className="inline w-3 h-3 mr-0.5" /> {_formatData(di)} → {_formatData(df)}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={isVendScope ? "Suas oportunidades" : "Indicações totais"} value={kpis.total_indicacoes} icon={Sparkles} />
        <KpiCard label="Em andamento" value={kpis.total_ativas} icon={TrendingUp} subtitle="Ativas no funil" />
        <KpiCard label={isVendScope ? "Você fechou" : "Contratos fechados"} value={kpis.total_fechadas} icon={Handshake} tone="emerald" subtitle={`Conversão: ${kpis.conversao_pct}%`} />
        <KpiCard label={isVendScope ? "Sua receita gerada" : "Receita CeL"} value={formatBRL(kpis.receita_total)} icon={DollarSign} tone="emerald" subtitle={`Ticket: ${formatBRL(kpis.ticket_medio)}`} />
      </div>

      <div className={`grid grid-cols-1 ${isVendScope ? "md:grid-cols-2" : "md:grid-cols-3"} gap-3`}>
        {!isVendScope && (
          <KpiCard label="Comissão indicadores" value={formatBRL(kpis.comissao_total)} icon={Percent} tone="amber" />
        )}
        <KpiCard label={isVendScope ? "Sua comissão (vendedora)" : "Comissão vendedoras CeL"} value={formatBRL(kpis.comissao_vendedora_total)} icon={Award} tone="sky-solid" />
        <KpiCard label="Perdidas no período" value={kpis.total_perdidas} icon={Users} />
      </div>

      {/* Iter 54 · Meta mensal individual (só vendedora CeL) */}
      {isVendScope && data.meta_mes && data.meta_mes.meta > 0 && (
        <div className="bg-gradient-to-br from-sky-600 to-sky-700 rounded-2xl p-5 text-white shadow-lg" data-testid="meta-mes-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider opacity-90">Meta do mês · {data.meta_mes.mes_ano}</div>
              <div className="text-4xl font-bold mt-1">{formatBRL(data.meta_mes.meta)}</div>
              <div className="text-sm mt-1 opacity-90">
                Realizado: <b>{formatBRL(data.meta_mes.realizado)}</b> · Faltam: <b>{formatBRL(data.meta_mes.gap)}</b>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase font-bold tracking-wider opacity-90">Progresso</div>
              <div className={`text-4xl font-bold mt-1 ${data.meta_mes.pct >= 100 ? "text-emerald-200" : ""}`}>{data.meta_mes.pct}%</div>
              {data.meta_mes.pct >= 100 && <div className="text-[11px] opacity-90 mt-0.5">🎉 Meta batida</div>}
            </div>
          </div>
          <div className="mt-3 bg-white/20 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all ${data.meta_mes.pct >= 100 ? "bg-emerald-300" : "bg-white"}`}
              style={{ width: `${Math.min(100, data.meta_mes.pct)}%` }}
            />
          </div>
        </div>
      )}
      {isVendScope && (!data.meta_mes || data.meta_mes.meta === 0) && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 text-amber-900 text-sm" data-testid="meta-mes-empty">
          <b>Você ainda não tem meta mensal definida.</b> Peça à Gestão CeL para configurar em <i>Admin CeL → Vendedoras CeL → botão Meta</i>.
        </div>
      )}

      {/* Funil */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-slate-600" />
          <h2 className="text-lg font-bold text-slate-900">Funil por estágio</h2>
          <span className="text-xs text-slate-500">(R$ contabilizado apenas a partir de <b>Proposta</b>)</span>
        </div>
        <div className="space-y-2">
          {Object.entries(funil).map(([est, info]) => {
            const meta = ESTAGIO_META[est] || { label: est, color: "bg-slate-400" };
            const pct = maxQtd > 0 ? (info.qtd / maxQtd) * 100 : 0;
            return (
              <div key={est} className="flex items-center gap-3" data-testid={`funil-row-${est}`}>
                <div className="w-44 text-xs text-slate-700 font-semibold truncate">{meta.label}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
                  <div className={`${meta.color} h-7 flex items-center justify-end pr-3 text-white text-xs font-bold rounded-full`} style={{ width: `${Math.max(pct, 8)}%` }}>
                    {info.qtd}
                  </div>
                </div>
                <div className="w-32 text-right text-xs text-slate-600 font-mono">
                  {info.com_valor ? formatBRL(info.valor) : <span className="italic text-slate-400">—</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Rankings tab-switcher (oculto para vendedora CeL — visão personalizada dela) */}
      {!isVendScope && (
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-slate-900">Rankings</h2>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="ranking-tabs">
            <button
              onClick={() => setRankingTab("fechamentos")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${rankingTab === "fechamentos" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="tab-fechamentos"
            >
              🏆 Fechamentos
            </button>
            <button
              onClick={() => setRankingTab("indicacoes")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${rankingTab === "indicacoes" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="tab-indicacoes"
            >
              🎯 Volume de indicações
            </button>
            {isGestaoOuDiretor && (
              <button
                onClick={() => setRankingTab("vendedoras")}
                className={`px-3 py-1 text-xs font-bold rounded-md transition ${rankingTab === "vendedoras" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                data-testid="tab-vendedoras"
              >
                👥 Vendedoras CeL
              </button>
            )}
          </div>
        </div>

        {rankingTab === "fechamentos" && (
          activeRanking.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhum contrato fechado no período.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Indicador</th>
                  <th className="py-2 pr-2">Papel</th>
                  <th className="py-2 pr-2 text-center">Fechados</th>
                  <th className="py-2 pr-2 text-right">Receita gerada</th>
                  <th className="py-2 pr-2 text-right">Comissão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeRanking.map((r, i) => (
                  <tr key={r.indicador_id || i} className="hover:bg-slate-50" data-testid={`rank-fech-${i}`}>
                    <td className="py-2 pr-2 font-bold text-slate-500">{i + 1}</td>
                    <td className="py-2 pr-2 font-semibold text-slate-800">{r.indicador_nome}</td>
                    <td className="py-2 pr-2 text-xs text-slate-500">{r.indicador_role}</td>
                    <td className="py-2 pr-2 text-center font-bold">{r.fechados}</td>
                    <td className="py-2 pr-2 text-right font-mono">{formatBRL(r.receita)}</td>
                    <td className="py-2 pr-2 text-right font-mono text-emerald-700 font-bold">{formatBRL(r.comissao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {rankingTab === "indicacoes" && (
          activeRanking.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhuma indicação no período.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Indicador</th>
                  <th className="py-2 pr-2">Papel</th>
                  <th className="py-2 pr-2 text-center">Total</th>
                  <th className="py-2 pr-2 text-center">Fechados</th>
                  <th className="py-2 pr-2 text-center">Perdidos</th>
                  <th className="py-2 pr-2 text-center">Conversão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeRanking.map((r, i) => {
                  const conv = r.total > 0 ? Math.round((r.fechados / r.total) * 100) : 0;
                  return (
                    <tr key={r.indicador_id || i} className="hover:bg-slate-50" data-testid={`rank-ind-${i}`}>
                      <td className="py-2 pr-2 font-bold text-slate-500">{i + 1}</td>
                      <td className="py-2 pr-2 font-semibold text-slate-800">{r.indicador_nome}</td>
                      <td className="py-2 pr-2 text-xs text-slate-500">{r.indicador_role}</td>
                      <td className="py-2 pr-2 text-center font-bold text-sky-700">{r.total}</td>
                      <td className="py-2 pr-2 text-center font-bold text-emerald-700">{r.fechados}</td>
                      <td className="py-2 pr-2 text-center text-red-600">{r.perdidos}</td>
                      <td className="py-2 pr-2 text-center font-bold">{conv}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {rankingTab === "vendedoras" && isGestaoOuDiretor && (
          activeRanking.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhuma vendedora CeL com movimentação no período.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Vendedora</th>
                  <th className="py-2 pr-2">UFs</th>
                  <th className="py-2 pr-2 text-center">Ativas</th>
                  <th className="py-2 pr-2 text-center">Fechados</th>
                  <th className="py-2 pr-2 text-center">Perdidos</th>
                  <th className="py-2 pr-2 text-right">Receita gerada</th>
                  <th className="py-2 pr-2 text-right">Comissão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeRanking.map((r, i) => (
                  <tr key={r.vendedora_id || i} className="hover:bg-slate-50" data-testid={`rank-vend-${i}`}>
                    <td className="py-2 pr-2 font-bold text-slate-500">{i + 1}</td>
                    <td className="py-2 pr-2">
                      <div className="font-semibold text-slate-800">{r.vendedora_nome}</div>
                      <div className="text-[11px] text-slate-500">{r.vendedora_email}</div>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex flex-wrap gap-0.5">
                        {(r.vendedora_ufs || []).map((uf) => <span key={uf} className="px-1 bg-sky-100 text-sky-800 text-[10px] font-mono font-bold rounded">{uf}</span>)}
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-center font-bold text-sky-700">{r.ativas}</td>
                    <td className="py-2 pr-2 text-center font-bold text-emerald-700">{r.fechados}</td>
                    <td className="py-2 pr-2 text-center text-red-600">{r.perdidos}</td>
                    <td className="py-2 pr-2 text-right font-mono">{formatBRL(r.receita)}</td>
                    <td className="py-2 pr-2 text-right font-mono text-sky-700 font-bold">{formatBRL(r.comissao_vendedora)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
      )}
    </div>
  );
}
